import { useCallback, useEffect, useState } from 'react';
import type {
  RoadmapItem,
  RoadmapTemplateOverride,
  RoadmapTemplatesData,
} from '@shared/roadmap';
import { api } from './api';

let templatesCache: RoadmapTemplatesData | null = null;
let templatesInflight: Promise<RoadmapTemplatesData> | null = null;

export async function loadRoadmapTemplates(): Promise<RoadmapTemplatesData> {
  if (templatesCache) return templatesCache;
  if (!templatesInflight) {
    templatesInflight = api.get<RoadmapTemplatesData>('/api/roadmap-templates').then((d) => {
      templatesCache = d;
      return d;
    });
  }
  return templatesInflight;
}

export function useRoadmapTemplates() {
  const [data, setData] = useState<RoadmapTemplatesData | null>(templatesCache);
  const [overrides, setOverrides] = useState<RoadmapTemplateOverride[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      const [d, o] = await Promise.all([
        loadRoadmapTemplates(),
        api.get<RoadmapTemplateOverride[]>('/api/roadmap-templates/overrides'),
      ]);
      setData(d);
      setOverrides(o);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e : new Error(String(e)));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { data, overrides, loading, error, refresh };
}

export function useDiagnosticRoadmap(diagnosticId: number | undefined) {
  const [items, setItems] = useState<RoadmapItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const refresh = useCallback(async () => {
    if (diagnosticId === undefined) return;
    try {
      setLoading(true);
      const r = await api.get<{ items: RoadmapItem[] }>(
        `/api/diagnostics/${diagnosticId}/roadmap`,
      );
      setItems(r.items);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e : new Error(String(e)));
    } finally {
      setLoading(false);
    }
  }, [diagnosticId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { items, loading, error, refresh };
}
